import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import type { Guid } from "@kiraah-shared/common";
import { MOBILE_MISSION_TASK_ROUTE_SEGMENTS, mobileMissionTasksUrl } from "../../mission-task/mobile-mission-task-route.provider";
import { createMobileMissionAppAccess } from "../../mission-task/helpers/mobile-mission-app-access";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import { MobileVoiceReportRecorderComponent, type MobileVoiceReportRecorderPhase } from "../mobile-voice-report-recorder/mobile-voice-report-recorder.component";
import { MobileVoiceReportValidationComponent } from "../mobile-voice-report-validation/mobile-voice-report-validation.component";
import { MobileVoiceReportService } from "../services/mobile-voice-report.service";
import type { IMobileVoiceReportTaskDraft, MobileVoiceReportWorkflowStep } from "../models/mobile-voice-report.models";

@Component({
  selector: "mobile-voice-report-workflow-page",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MobileVoiceReportRecorderComponent,
    MobileVoiceReportValidationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-voice-report-workflow-page.component.html",
  styleUrl: "./mobile-voice-report-workflow-page.component.scss",
})
export class MobileVoiceReportWorkflowPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly voiceReportService = inject(MobileVoiceReportService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleAccess = inject(MobileModuleAccessService);
  private readonly missionNavSegments = inject(MOBILE_MISSION_TASK_ROUTE_SEGMENTS);

  protected readonly access = createMobileMissionAppAccess(this.moduleAccess, this.missionNavSegments).voiceReportAccess;

  protected readonly step = signal<MobileVoiceReportWorkflowStep>("recorder");
  protected readonly missionUid = signal<Guid | null>(null);
  protected readonly missionLabel = signal<string | null>(null);
  protected readonly sessionUid = signal<Guid | undefined>(undefined);
  protected readonly reportHtml = signal("");
  protected readonly tasks = signal<readonly IMobileVoiceReportTaskDraft[]>([]);
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly isSubmitting = signal(false);

  protected readonly recorderPhase = signal<MobileVoiceReportRecorderPhase | null>(null);

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const uid = params.get("missionUid");
      this.missionUid.set(uid as Guid);
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams) => {
      const label = queryParams.get("missionLabel");
      this.missionLabel.set(label?.trim() || null);
    });
  }

  protected onRecordingFinished(audio: Blob): void {
    if (!this.access().hasUpdateAccess) {
      return;
    }
    const missionUid = this.missionUid();
    if (!missionUid) {
      this.errorMessageKey.set("mobile.voiceReport.error.missingMission");
      this.step.set("error");
      return;
    }

    this.step.set("processing");
    this.recorderPhase.set("processing");

    this.voiceReportService
      .processAudio(missionUid, audio)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.sessionUid.set(result.SessionUid);
          this.reportHtml.set(result.ReportHtml ?? "");
          this.tasks.set(result.Tasks ?? []);
          this.recorderPhase.set(null);
          this.step.set("validation");
        },
        error: (err: unknown) => {
          this.recorderPhase.set(null);
          this.errorMessageKey.set(this.resolveErrorKey(err));
          this.step.set("error");
        },
      });
  }

  protected onRecordError(key: string): void {
    this.errorMessageKey.set(key);
    this.step.set("error");
  }

  protected onValidated(payload: { reportHtml: string; tasks: IMobileVoiceReportTaskDraft[] }): void {
    const missionUid = this.missionUid();
    if (!missionUid || this.isSubmitting() || !this.access().hasUpdateAccess) {
      return;
    }

    this.isSubmitting.set(true);
    this.voiceReportService
      .validate({
        MissionUid: missionUid,
        SessionUid: this.sessionUid(),
        ReportHtml: payload.reportHtml,
        Tasks: payload.tasks,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          void this.router.navigateByUrl(mobileMissionTasksUrl(this.missionNavSegments));
        },
        error: (err: unknown) => {
          this.isSubmitting.set(false);
          this.errorMessageKey.set(this.resolveErrorKey(err));
          this.step.set("error");
        },
      });
  }

  protected backToList(): void {
    void this.router.navigateByUrl(mobileMissionTasksUrl(this.missionNavSegments));
  }

  protected retryRecorder(): void {
    this.errorMessageKey.set(null);
    this.reportHtml.set("");
    this.tasks.set([]);
    this.sessionUid.set(undefined);
    this.step.set("recorder");
  }

  protected errorMessage(): string {
    const key = this.errorMessageKey();
    if (!key) {
      return "";
    }
    return this.translate.instant(key);
  }

  private resolveErrorKey(err: unknown): string {
    if (err instanceof Error && err.message.startsWith("mobile.")) {
      return err.message;
    }
    return "mobile.voiceReport.error.generic";
  }
}
